/**
 * Advanced Analytics Dashboard
 * Time-series charts, trending data, KPI visualization
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, eachDayOfInterval, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  TrendingUp, TrendingDown, BarChart3, ArrowLeft, RefreshCw, 
  Calendar, Users, Briefcase, CheckCircle, Clock, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1F7A78', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function AdvancedAnalyticsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Analytics data
  const [jobTrends, setJobTrends] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [contractorPerformance, setContractorPerformance] = useState([]);
  const [responseTimeTrend, setResponseTimeTrend] = useState([]);
  const [kpis, setKpis] = useState({});
  
  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/advanced`, {
        headers,
        params: { date_from: dateFrom, date_to: dateTo }
      });
      
      if (res.data.ok) {
        setJobTrends(res.data.job_trends || []);
        setStatusBreakdown(res.data.status_breakdown || []);
        setContractorPerformance(res.data.contractor_performance || []);
        setResponseTimeTrend(res.data.response_time_trend || []);
        setKpis(res.data.kpis || {});
      }
    } catch (err) {
      console.error('Load analytics error:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);
  
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);
  
  const KPICard = ({ title, value, change, icon: Icon, color }) => (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span>{Math.abs(change)}% vs last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-[#1F7A78]" />
                Advanced Analytics
              </h1>
              <p className="text-sm text-slate-500">Time-series trends and KPI analysis</p>
            </div>
          </div>
          
          <Button 
            onClick={loadAnalytics} 
            disabled={loading}
            className="bg-[#1F7A78] hover:bg-[#16615f]"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Date Filter */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <label className="text-xs text-slate-500">From</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
              </div>
              <div>
                <label className="text-xs text-slate-500">To</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
              </div>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => { setDateFrom(format(subDays(new Date(), 7), 'yyyy-MM-dd')); setDateTo(format(new Date(), 'yyyy-MM-dd')); }}>7 Days</Button>
                <Button variant="outline" size="sm" onClick={() => { setDateFrom(format(subDays(new Date(), 30), 'yyyy-MM-dd')); setDateTo(format(new Date(), 'yyyy-MM-dd')); }}>30 Days</Button>
                <Button variant="outline" size="sm" onClick={() => { setDateFrom(format(subDays(new Date(), 90), 'yyyy-MM-dd')); setDateTo(format(new Date(), 'yyyy-MM-dd')); }}>90 Days</Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard title="Total Jobs" value={kpis.total_jobs || 0} change={kpis.jobs_change} icon={Briefcase} color="bg-[#1F7A78]" />
              <KPICard title="Completed" value={kpis.completed_jobs || 0} change={kpis.completed_change} icon={CheckCircle} color="bg-emerald-500" />
              <KPICard title="Avg Response Time" value={`${kpis.avg_response_hours || 0}h`} change={kpis.response_change} icon={Clock} color="bg-blue-500" />
              <KPICard title="Active Contractors" value={kpis.active_contractors || 0} change={kpis.contractors_change} icon={Users} color="bg-purple-500" />
            </div>
            
            {/* Job Volume Trend Chart */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#1F7A78]" />
                  Job Volume Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={jobTrends}>
                      <defs>
                        <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1F7A78" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1F7A78" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="created" name="Created" stroke="#1F7A78" fillOpacity={1} fill="url(#colorJobs)" />
                      <Area type="monotone" dataKey="completed" name="Completed" stroke="#10b981" fillOpacity={0.3} fill="#10b981" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Two Column Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Breakdown Pie */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Job Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusBreakdown}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          fill="#8884d8"
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="status"
                          label={({ status, percent }) => `${status} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {statusBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Contractor Performance Bar */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Top Contractors (Jobs Completed)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={contractorPerformance} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip />
                        <Bar dataKey="completed" name="Completed" fill="#1F7A78" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Response Time Trend */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  Average Response Time Trend (Hours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={responseTimeTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="avg_hours" name="Avg Response (hours)" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
